import { AesKwKeyWrapper } from './keys.js'
import { CryptoError } from './errors.js'
import { bytesToBase64Url, base64UrlToBytes } from './encoding.js'
import { WebCryptoAead, importAesGcmKey } from './aead.js'
import { canonicalizeJcs } from './jcs.js'
import { randomBytes } from './random.js'

/**
 * Vault payload envelopes — PRD §8.5, §11.2.
 * Payload bytes are encrypted client-side with the vault DEK; AAD binds the
 * profile, vault, object and revision so ciphertext cannot be moved between
 * records. Schema/format scaffolding only — the vault adapter lands in S0.5.
 */

export const VAULT_PAYLOAD_PROFILE_V1 = 'signbento-vault-v1'
export const RECOVERY_SECRET_PROFILE_V1 = 'signbento-recovery-secret-v1'

export interface VaultPayloadContext {
  readonly vault_id: string
  readonly object_id: string
  readonly revision: number
}

export interface VaultPayloadEnvelope {
  readonly profile: string
  readonly key_id: string
  /** Base64url without padding. */
  readonly nonce: string
  /** Base64url without padding; ciphertext includes the GCM tag. */
  readonly ciphertext: string
}

const textEncoder = new TextEncoder()
const aead = new WebCryptoAead()

function assertContext(context: VaultPayloadContext): void {
  if (
    context.vault_id.length === 0 ||
    context.object_id.length === 0 ||
    !Number.isInteger(context.revision) ||
    context.revision < 0
  ) {
    throw new CryptoError('CRYPTO_INVALID_INPUT', 'vault context requires ids and a revision')
  }
}

function aadFor(profile: string, context: VaultPayloadContext): Uint8Array {
  return textEncoder.encode(canonicalizeJcs({ profile, ...context }))
}

export interface EncryptVaultPayloadInput {
  readonly key: CryptoKey
  readonly key_id: string
  readonly content: Uint8Array | string
  readonly context: VaultPayloadContext
}

export async function encryptVaultPayload(
  input: EncryptVaultPayloadInput,
): Promise<VaultPayloadEnvelope> {
  assertContext(input.context)
  if (input.key_id.length === 0) {
    throw new CryptoError('CRYPTO_INVALID_INPUT', 'key_id is required')
  }
  const plaintext =
    typeof input.content === 'string' ? textEncoder.encode(input.content) : input.content
  const sealed = await aead.seal(
    plaintext,
    aadFor(VAULT_PAYLOAD_PROFILE_V1, input.context),
    input.key,
  )
  const nonce = sealed.subarray(0, 12)
  const ciphertext = sealed.subarray(12)
  return {
    profile: VAULT_PAYLOAD_PROFILE_V1,
    key_id: input.key_id,
    nonce: bytesToBase64Url(nonce),
    ciphertext: bytesToBase64Url(ciphertext),
  }
}

export interface DecryptVaultPayloadInput {
  readonly key: CryptoKey
  readonly envelope: VaultPayloadEnvelope
  readonly context: VaultPayloadContext
}

export async function decryptVaultPayload(input: DecryptVaultPayloadInput): Promise<Uint8Array> {
  assertContext(input.context)
  if (input.envelope.profile !== VAULT_PAYLOAD_PROFILE_V1) {
    throw new CryptoError('CRYPTO_INVALID_INPUT', 'unsupported vault payload profile')
  }
  const nonce = base64UrlToBytes(input.envelope.nonce)
  const ciphertext = base64UrlToBytes(input.envelope.ciphertext)
  const sealed = new Uint8Array(nonce.byteLength + ciphertext.byteLength)
  sealed.set(nonce, 0)
  sealed.set(ciphertext, nonce.byteLength)
  return aead.open(sealed, aadFor(input.envelope.profile, input.context), input.key)
}

/**
 * Wrapped recovery-secret scaffolding (consumed by the .agbackup key schedule
 * in S2.5). The secret is random bytes wrapped with the platform KEK.
 */
export interface WrappedSecretScaffold {
  readonly profile: string
  readonly key_id: string
  readonly wrapped: string
}

export function generateRecoverySecret(): Uint8Array {
  return randomBytes(32)
}

export async function wrapRecoverySecret(
  secret: Uint8Array,
  kek: CryptoKey,
  key_id: string,
): Promise<WrappedSecretScaffold> {
  if (secret.byteLength !== 32) {
    throw new CryptoError('CRYPTO_INVALID_INPUT', 'recovery secret must be 32 bytes')
  }
  const key = await importAesGcmKey(secret, true)
  const wrapped = await new AesKwKeyWrapper().wrap(key, kek)
  return {
    profile: RECOVERY_SECRET_PROFILE_V1,
    key_id,
    wrapped: bytesToBase64Url(wrapped),
  }
}

export async function unwrapRecoverySecret(
  scaffold: WrappedSecretScaffold,
  kek: CryptoKey,
): Promise<Uint8Array> {
  if (scaffold.profile !== RECOVERY_SECRET_PROFILE_V1) {
    throw new CryptoError('CRYPTO_INVALID_INPUT', 'unsupported recovery secret profile')
  }
  const key = await new AesKwKeyWrapper().unwrapDek(base64UrlToBytes(scaffold.wrapped), kek)
  const raw = await globalThis.crypto.subtle.exportKey('raw', key)
  return new Uint8Array(raw)
}
